// ======================================================================
// FILE: src/modules/wms/team/pages/TeamFormPage.jsx
// MODULE: Work Management System (WMS)
// FEATURE: Team Management
// PURPOSE:
//   Create / Edit Team
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsTeamService
from "../../services/wmsTeamService";

export default function TeamFormPage() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [saving, setSaving] =
    useState(false);

  const [form, setForm] =
    useState({

      team_code: "",
      team_name: "",

      //department_id: "",

      completion_mode:
        "ANY_MEMBER",

      required_completion_percentage:
        "",

      description: "",
      remarks: "",
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (!isEdit) return;

    loadTeam();

  }, [id]);

  async function loadTeam() {

    try {

      const data =
        await wmsTeamService.getTeam(id);

      setForm({

        team_code:
          data.team_code || "",

        team_name:
          data.team_name || "",

        //department_id:
        //  data.department_id || "",

        completion_mode:
          data.completion_mode ||
          "ANY_MEMBER",

        required_completion_percentage:
          data.required_completion_percentage || "",

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load team"
      );
    }
  }

  // ==========================================================
  // CHANGE
  // ==========================================================

  function handleChange(
    field,
    value
  ) {

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================

  function validate() {

    if (!form.team_code?.trim()) {

      alert(
        "Team Code is required"
      );

      return false;
    }

    if (!form.team_name?.trim()) {

      alert(
        "Team Name is required"
      );

      return false;
    }

    if (!form.completion_mode) {

      alert(
        "Completion Mode is required"
      );

      return false;
    }

    if (
      form.completion_mode ===
      "PERCENTAGE"
    ) {

      const pct =
        Number(
          form.required_completion_percentage
        );

      if (
        !pct ||
        pct < 1 ||
        pct > 100
      ) {

        alert(
          "Percentage must be between 1 and 100"
        );

        return false;
      }
    }

    return true;
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!validate()) {
      return;
    }

    try {

      setSaving(true);

      const payload = {

        team_code:
          form.team_code,

        team_name:
          form.team_name,

        //department_id:
        //  form.department_id || null,

        completion_mode:
          form.completion_mode,

        required_completion_percentage:
          form.completion_mode ===
          "PERCENTAGE"
            ? Number(
                form.required_completion_percentage
              )
            : null,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (isEdit) {

        await wmsTeamService.updateTeam(
          id,
          payload
        );

      } else {

        await wmsTeamService.createTeam(
          payload
        );
      }

      navigate(
        "/wms/teams"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      <div className="card">

        <div className="card-header">

          <h3 className="card-title">

            {isEdit
              ? "Edit Team"
              : "Create Team"}

          </h3>

        </div>

        <div className="card-body">

          <div className="row">

            {/* Team Code */}

            <div className="col-md-4 mb-3">

              <label>
                Team Code *
              </label>

              <input
                className="form-control"
                value={
                  form.team_code
                }
                onChange={(e) =>
                  handleChange(
                    "team_code",
                    e.target.value
                  )
                }
              />

            </div>

            {/* Team Name */}

            <div className="col-md-8 mb-3">

              <label>
                Team Name *
              </label>

              <input
                className="form-control"
                value={
                  form.team_name
                }
                onChange={(e) =>
                  handleChange(
                    "team_name",
                    e.target.value
                  )
                }
              />

            </div>

            

            {/* Completion Mode */}

            <div className="col-md-4 mb-3">

              <label>
                Completion Mode *
              </label>

              <select
                className="form-control"
                value={
                  form.completion_mode
                }
                onChange={(e) =>
                  handleChange(
                    "completion_mode",
                    e.target.value
                  )
                }
              >

                <option value="ANY_MEMBER">
                  Any Member
                </option>

                <option value="ALL_MEMBERS">
                  All Members
                </option>

                <option value="PERCENTAGE">
                  Percentage
                </option>

              </select>

            </div>

            {/* Percentage */}

            {form.completion_mode ===
              "PERCENTAGE" && (

              <div className="col-md-4 mb-3">

                <label>
                  Required Completion %
                </label>

                <input
                  type="number"
                  min="1"
                  max="100"
                  className="form-control"
                  value={
                    form.required_completion_percentage
                  }
                  onChange={(e) =>
                    handleChange(
                      "required_completion_percentage",
                      e.target.value
                    )
                  }
                />

              </div>
            )}

            {/* Description */}

            <div className="col-md-12 mb-3">

              <label>
                Description
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.description
                }
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
              />

            </div>

            {/* Remarks */}

            <div className="col-md-12 mb-3">

              <label>
                Remarks
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.remarks
                }
                onChange={(e) =>
                  handleChange(
                    "remarks",
                    e.target.value
                  )
                }
              />

            </div>

          </div>

        </div>

        <div className="card-footer">

          <button
            className="btn btn-primary me-2"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : "Save"}
          </button>

          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate(
                "/wms/teams"
              )
            }
          >
            Cancel
          </button>

        </div>

      </div>

    </div>
  );
}